import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, X, ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCart } from '../context/CartContext';
import { orderPath } from '../lib/orderPath';

export default function CartPage() {
  const navigate = useNavigate();
  const {
    cartItems,
    restaurantSlug,
    updateQuantity,
    removeFromCart,
    getCartTotal,
    getCartCount,
  } = useCart();

  // Menu prices; the server prices the order (promo, the restaurant's own tax rate) at checkout.
  const subtotal = getCartTotal();
  const itemCount = getCartCount();

  const handleQuantityChange = (cartItemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(cartItemId);
    } else {
      updateQuantity(cartItemId, newQuantity);
    }
  };

  const handleBackToMenu = () => {
    if (restaurantSlug) {
      navigate(orderPath(`storefront/${restaurantSlug}`));
    } else {
      navigate(orderPath(''));
    }
  };

  const handleCheckout = () => {
    navigate(orderPath('checkout'));
  };

  // Empty state
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="h-12 w-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some delicious items to get started!</p>
          <Button
            onClick={handleBackToMenu}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Browse Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackToMenu}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Back to Menu</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              Cart ({itemCount})
            </h1>
          </div>
        </div>
      </div>

      {/* Cart Items */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="space-y-4">
          {cartItems.map((item) => (
            <Card key={item.cartItemId} className="bg-white">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Item Image */}
                  <div className="h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-3xl">
                        🍽️
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      <button
                        onClick={() => removeFromCart(item.cartItemId)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                        aria-label="Remove item"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Modifiers */}
                    {item.modifiers && item.modifiers.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {item.modifiers.map((modifier, idx) => (
                          <div key={idx} className="text-sm text-gray-600">
                            <span className="font-medium">{modifier.groupName}:</span>{' '}
                            {modifier.options.map(opt => opt.name).join(', ')}
                            {modifier.options.some(opt => opt.price > 0) && (
                              <span className="text-gray-500">
                                {' '}(+${modifier.options.reduce((sum, opt) => sum + opt.price, 0).toFixed(2)})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Special Instructions */}
                    {item.specialInstructions && (
                      <div className="text-sm text-gray-600 mb-2 italic">
                        Note: {item.specialInstructions}
                      </div>
                    )}

                    {/* Dietary Badges */}
                    {(item.is_spicy || item.is_vegetarian || item.is_vegan || item.is_gluten_free) && (
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        {item.is_spicy && <Badge variant="outline" className="text-xs">Spicy</Badge>}
                        {item.is_vegetarian && <Badge variant="outline" className="text-xs">Vegetarian</Badge>}
                        {item.is_vegan && <Badge variant="outline" className="text-xs">Vegan</Badge>}
                        {item.is_gluten_free && <Badge variant="outline" className="text-xs">GF</Badge>}
                      </div>
                    )}

                    {/* Quantity Controls & Price */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleQuantityChange(item.cartItemId, item.quantity - 1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="text-base font-semibold w-8 text-center">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleQuantityChange(item.cartItemId, item.quantity + 1)}
                          disabled={item.quantity >= 99}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="text-right">
                        <div className="font-bold text-lg text-gray-900">
                          ${item.totalPrice.toFixed(2)}
                        </div>
                        {item.quantity > 1 && (
                          <div className="text-xs text-gray-500">
                            ${(item.totalPrice / item.quantity).toFixed(2)} each
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Cart Summary */}
        <Card className="mt-6 bg-white">
          <CardContent className="p-4">
            <h2 className="font-semibold text-gray-900 mb-3">Order Summary</h2>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-gray-600">
                <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              
              <div className="flex items-center justify-between text-gray-600">
                <span>Tax</span>
                <span className="text-sm" data-testid="cart-tax-at-checkout">Calculated at checkout</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add More Items Button */}
        <Button
          variant="outline"
          onClick={handleBackToMenu}
          className="w-full mt-4"
        >
          Add More Items
        </Button>
      </div>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-gray-600">Subtotal</div>
              <div className="text-2xl font-bold text-gray-900">
                ${subtotal.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">+ tax at checkout</div>
            </div>
            <Button
              onClick={handleCheckout}
              className="bg-blue-600 hover:bg-blue-700 h-14 px-8 text-lg font-semibold"
            >
              Checkout →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
